import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';

export default function Navbar() {
  const [scrollY, setScrollY] = useState(0);
  const [menuOpen, setMenuOpen] = useState(false);
  const [isDark, setIsDark] = useState(true);
  const [navBg, setNavBg] = useState('transparent');
  const location = useLocation();

  const detectSection = useCallback(() => {
    const currentScrollY = window.scrollY;
    setScrollY(currentScrollY);

    if (currentScrollY < 80) {
      setNavBg('transparent');
      const lightBgPages = ['/team', '/contact'];
      if (lightBgPages.includes(location.pathname)) {
        setIsDark(false);
      } else {
        const firstSection = document.querySelector('section');
        if (firstSection && firstSection.dataset.navbarLight === 'true') {
          setIsDark(false);
        } else {
          setIsDark(true);
        }
      }
    } else {
      const sections = document.querySelectorAll('section');
      let currentSection = null;

      sections.forEach((section) => {
        const rect = section.getBoundingClientRect();
        if (rect.top <= 80 && rect.bottom >= 80) {
          currentSection = section;
        }
      });

      if (currentSection) {
        const computedBg = window.getComputedStyle(currentSection).backgroundColor;
        setNavBg(computedBg);

        if (currentSection.dataset.navbarLight === 'true') {
          setIsDark(false);
        } else {
          const rgb = computedBg.match(/\d+/g);
          if (rgb) {
            const brightness =
              (parseInt(rgb[0]) * 299 +
                parseInt(rgb[1]) * 587 +
                parseInt(rgb[2]) * 114) /
              1000;
            setIsDark(brightness < 128);
          }
        }
      }
    }
  }, [location.pathname]);

  useEffect(() => {
    window.addEventListener('scroll', detectSection, { passive: true });
    return () => window.removeEventListener('scroll', detectSection);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detectSection]);

  useEffect(() => {
    setMenuOpen(false);
    setNavBg('transparent');
    setScrollY(0);
    const timer = setTimeout(() => {
      detectSection();
    }, 100);
    return () => clearTimeout(timer);
  }, [location.pathname, detectSection]);

  const isTransparent = scrollY < 80;

  const linkColor = isDark
    ? 'text-white/80 hover:text-white'
    : 'text-[#1e3019]/70 hover:text-[#1e3019]';
  const logoColor = isDark ? 'text-white' : 'text-[#1e3019]';
  const buttonBorder = isDark
    ? 'border-white text-white hover:bg-white hover:text-[#2d3828]'
    : 'border-[#1e3019] text-[#1e3019] hover:bg-[#1e3019] hover:text-white';

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 px-4 sm:px-6 pt-4 sm:pt-5">
      <div
        className={`max-w-7xl mx-auto flex items-center justify-between py-3 sm:py-4 px-5 sm:px-8 rounded-2xl transition-all duration-500
          ${isTransparent
            ? 'bg-transparent shadow-none border-transparent'
            : 'shadow-2xl border border-white/10'
          }`}
        style={{
          backgroundColor: isTransparent ? 'transparent' : navBg,
        }}
      >
        <Link
          to="/"
          className={`font-body font-semibold text-lg sm:text-xl tracking-tight transition-colors duration-300 ${logoColor}`}
        >
          Carbonetics
        </Link>

        <div className="hidden md:flex items-center gap-6 lg:gap-8">
          {['Services', 'About', 'Team'].map((item) => (
            <Link
              key={item}
              to={`/${item.toLowerCase()}`}
              className={`font-body text-sm font-medium transition-colors duration-300 ${linkColor}`}
            >
              {item}
            </Link>
          ))}
        </div>

        <div className="hidden md:block">
          <Link
            to="/contact"
            className={`font-body text-sm font-medium px-4 lg:px-5 py-2 lg:py-2.5 rounded-full border transition-all duration-300 ${buttonBorder}`}
          >
            Contact Us
          </Link>
        </div>

        <button
          className={`md:hidden p-1 transition-colors duration-300 ${logoColor}`}
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="Toggle menu"
        >
          {menuOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {menuOpen && (
        <div className="md:hidden mt-2 bg-[#2d3828] rounded-2xl px-4 pb-6 flex flex-col gap-2">
          {['Services', 'About', 'Team'].map((item) => (
            <Link
              key={item}
              to={`/${item.toLowerCase()}`}
              className="text-white/80 hover:text-white font-body text-sm font-medium py-3 border-b border-white/10"
            >
              {item}
            </Link>
          ))}
          <Link
            to="/contact"
            className="mt-3 text-center border border-white text-white rounded-full px-5 py-2.5 text-sm font-medium hover:bg-white hover:text-[#2d3828] transition-all"
          >
            Contact Us
          </Link>
        </div>
      )}
    </nav>
  );
}